import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { CampaignListItemDto } from '@shared/services/apiService';
import type { AssistantMessage, WorkspaceOperation } from './workspaceApi';
import { useWorkspaceScope } from './useWorkspaceScope';

type Conversation = {
  campaign: Pick<CampaignListItemDto, 'id' | 'title'> | null;
  provider: string;
  model: string;
  messages: AssistantMessage[];
  text: string;
  submittedOperation: WorkspaceOperation | null;
  error: string;
  working: boolean;
};
const empty = (): Conversation => ({
  campaign: null,
  provider: '',
  model: '',
  messages: [],
  text: '',
  submittedOperation: null,
  error: '',
  working: false,
});
const useStateOwner = () => {
  const [state, setState] = useState(empty);
  const generation = useRef(0);
  const busy = useRef(false);
  const lastRequest = useRef<{ key: string; id: string } | null>(null);
  useEffect(
    () => () => {
      generation.current++;
      busy.current = false;
    },
    [],
  );
  return { state, setState, generation, busy, lastRequest };
};
const Context = createContext<ReturnType<typeof useStateOwner> | null>(null);
const Owner = ({ children }: { children: ReactNode }) => {
  const value = useStateOwner();
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

/** Memory only: a tenant/account change destroys the conversation and fences late responses. */
export const AskConversationProvider = ({ children }: { children: ReactNode }) => {
  const scope = useWorkspaceScope();
  return <Owner key={scope}>{children}</Owner>;
};
export const useAskConversation = () => {
  const context = useContext(Context);
  const local = useStateOwner();
  return context ?? local;
};
